import type { ServicePageData } from './types';

const featherFlags: ServicePageData = {
  slug: 'feather-flags',
  eyebrow: 'Display',
  eyebrowTo: '/services/all-display',
  title: 'Get noticed with',
  titleAccent: 'printed flags.',
  intro:
    'Feather and teardrop flags are the fastest way to put your brand at eye level outdoors. Lightweight, quick to put up and take down, and printed full colour on both sides so your message reads from any direction.',
  heroImage: '/home/display/oc-flag.jpg',

  useCases: [
    { title: 'Forecourts and car parks', text: 'Mark an entrance or point drivers towards your pitch from the road.' },
    { title: 'Shop fronts', text: 'A moving splash of colour and branding outside your door.' },
    { title: 'Markets and outdoor events', text: 'Help customers spot your stall from across a busy site.' },
    { title: 'Exhibitions and trade shows', text: 'Stand taller than the neighbouring stands without a full stand build.' },
    { title: 'Estate agents and site launches', text: 'Flag up a show home or new development for the weekend.' },
    { title: 'Sports days and fun runs', text: 'Mark a start line, finish line or sponsor area.' },
  ],

  optionsHeading: 'Choose your flag',
  optionsIntro: 'Three shapes and a choice of base, so your flag suits the ground and the wind on the day.',
  options: [
    {
      id: 'feather',
      title: 'Feather flag',
      lead: 'The curved, sweeping shape most people picture first.',
      body: 'Tall and narrow with a gentle curve along one edge, so it catches the wind and moves. Reads well from a distance and works alone or in a row.',
      image: '/home/pavement-signs/pavement-sign-wildjac.jpg',
      specs: [
        { label: 'Shape', value: 'Curved leading edge, straight trailing edge.' },
        { label: 'Fabric', value: 'Lightweight polyester, printed full colour, dye sublimated through the material.' },
        { label: 'Print', value: 'Double-sided so the design reads correctly from both directions.' },
      ],
    },
    {
      id: 'teardrop',
      title: 'Teardrop flag',
      lead: 'A wider, more stable shape for a bolder graphic.',
      body: 'Rounded at the top and tapered at the base, giving a larger panel to work with than a feather flag. Sits a little steadier in a breeze thanks to the wider profile.',
      image: '/home/pavement-signs/pavement-sign-hkh.jpg',
      specs: [
        { label: 'Shape', value: 'Rounded top, tapered lower panel.' },
        { label: 'Fabric', value: 'Lightweight polyester, printed full colour, dye sublimated through the material.' },
        { label: 'Print', value: 'Double-sided as standard.' },
      ],
    },
    {
      id: 'straight',
      title: 'Straight or rectangular flag',
      lead: 'A classic rectangle for a more formal, corporate look.',
      body: 'The traditional flag shape, often used alongside a logo pole or in matched pairs either side of an entrance.',
      image: '/home/pavement-signs/pavement-sign-salon4.jpg',
      specs: [
        { label: 'Shape', value: 'Straight rectangular panel.' },
        { label: 'Fabric', value: 'Lightweight polyester, printed full colour.' },
        { label: 'Print', value: 'Single or double-sided.' },
      ],
    },
    {
      id: 'bases',
      title: 'Bases and fixings',
      lead: 'Choose the base to suit your ground and how often the flag moves.',
      body: 'The pole and flag are the same across the range; the base is what changes. Pick whichever suits the surface the flag stands on and whether it is going up once or every weekend.',
      image: '/home/pavement-signs/pavement-sign-tattoo.jpg',
      specs: [
        { label: 'Ground spike', value: 'For grass, soil or gravel. Pushes or hammers in, quickest to set up outdoors.' },
        { label: 'Cross base', value: 'A flat weighted foot for hard standing such as paving or tarmac.' },
        { label: 'Water-fill base', value: 'Fill with water on site for extra weight, then empty for transport.' },
        { label: 'Wall bracket', value: 'Fixes the pole to a wall or fascia where a freestanding base is not practical.' },
      ],
    },
  ],

  customisation: [
    {
      title: 'Dye sublimation print',
      text: 'Full-colour artwork is printed then heat-transferred into the fabric itself, so the colour will not crack, peel or fade the way a surface print would.',
    },
    {
      title: 'Double-sided as standard',
      text: 'Your design is mirrored through the fabric so it reads correctly whichever way the flag is facing.',
    },
  ],

  gallery: [
    '/home/display/oc-flag.jpg',
    '/home/pavement-signs/pavement-sign-wildjac.jpg',
    '/home/pavement-signs/pavement-sign-hkh.jpg',
    '/home/pavement-signs/pavement-sign-salon4.jpg',
    '/home/pavement-signs/pavement-sign-tattoo.jpg',
    '/home/pavement-signs/pavement-sign-atelier.jpg',
    '/home/pavement-signs/pavement-sign-petalbar.jpg',
  ],

  info: [
    {
      title: 'Supply only',
      text: 'Flags arrive with clear instructions and go up in minutes. No tools needed for a ground spike or cross base.',
      bullets: ['Collect from Kidderminster', 'Delivery from £10'],
    },
    {
      title: 'Care',
      text: 'Take the flag down in high winds or heavy rain if it is left unattended, and let it dry before packing it away to stop the fabric marking.',
    },
    {
      title: 'Turnaround',
      text: 'Production is 5 to 10 working days from approval. Free design mockup and quote before you commit to anything.',
    },
  ],

  faqs: [
    {
      q: 'Which base should I choose?',
      a: 'Ground spike for grass or soil, cross base or water-fill base for paving and tarmac, and a wall bracket where you want the flag fixed to a building rather than freestanding.',
    },
    {
      q: 'Can I print on both sides?',
      a: 'Yes, and it is standard on most orders. The design is mirrored so it reads the right way round from either side.',
    },
    {
      q: 'Do the flags come down easily for storage?',
      a: 'Yes. The pole sections pull apart and the flag folds down small, so the whole thing fits in a carry bag between uses.',
    },
  ],

  cta: {
    title: 'Get your free flag quote and mockup.',
    text: 'Tell us where it is going up and we will recommend the shape and base to suit.',
    image: '/home/display/oc-flag.jpg',
  },
};

export default featherFlags;
